import React from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import InfiniteScroll from "react-infinite-scroll-component";

import { Button, Checkbox, Typography } from "@material-ui/core";

import ContentStudioEquipmentStore from "stores/ContentStudioEquipmentStore";

import Row from "components/Form/Row";
import Search from "components/Input/Search";
import Loader from "components/Layout/Loader";
import { SNACKBAR_TYPES } from "components/Snackbar";
import Placeholder from "components/Placeholder";
import SortingControl from "components/SortingControl";
import TrimmedText from "components/TrimmedText";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { DictionaryItem } from "constants/models";
import { TableSortType } from "stores/templates/TableStore";

import {
  Container,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  EquipmentsContainer,
  EquipmentWrapper,
  EquipmentInfo,
  EquipmentIcon,
  ScrollLoader,
  TitleContainer,
  SubtitleContainer,
} from "./styles";

type Props = {
  onClose: () => void;
  onSave: (newEquipment: DictionaryItem[]) => void;
  options: DictionaryItem[];
} & SnackbarContextProps;

const FORM_INFORMATION_HEIGHT = "450px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class EquipmentForm extends React.Component<Props> {
  @observable newEquipment: DictionaryItem[] = [];

  componentDidMount() {
    ContentStudioEquipmentStore.fetch();
  }

  componentWillUnmount() {
    ContentStudioEquipmentStore.resetPage();
    ContentStudioEquipmentStore.clearSearch();
  }

  fetchMoreEquipments = async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioEquipmentStore.fetchMoreEquipments();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onChangeCheckbox = (option: DictionaryItem) => () => {
    let items = this.newEquipment || [];

    if (items.find(item => item.value.id === option.value.id)) {
      items = items.filter(
        (item: DictionaryItem) => item.value.id !== option.value.id,
      );
    } else {
      items.push(option);
    }

    this.newEquipment = items;
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentStudioEquipmentStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentStudioEquipmentStore.fetch();
  };

  onSearchClear = () => {
    ContentStudioEquipmentStore.clearSearch();
    ContentStudioEquipmentStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentStudioEquipmentStore.applyOrder(value);
    ContentStudioEquipmentStore.resetPage();
    ContentStudioEquipmentStore.fetch();
  };

  onSaveNewEquipment = () => {
    this.props.onSave(this.newEquipment);
  };

  isOptionChecked = (option: DictionaryItem) =>
    !!this.newEquipment.find(
      equipment => equipment.value.id === option.value.id,
    );

  render() {
    const { onClose, options } = this.props;

    return (
      <>
        <Container>
          <Row>
            <TitleContainer>
              <Typography variant="h1" color="textPrimary">
                Add Equipments
              </Typography>
              {!!this.newEquipment?.length && (
                <SubtitleContainer>
                  <Typography variant="subtitle1" color="textPrimary">
                    {`${this.newEquipment.length} items selected`}
                  </Typography>
                </SubtitleContainer>
              )}
            </TitleContainer>
          </Row>

          <Row>
            <Search
              initValue={ContentStudioEquipmentStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={
                    ContentStudioEquipmentStore.orderContext.appliedOrderInfo
                  }
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
            </SearchControls>
          </Row>

          <Row>
            {!ContentStudioEquipmentStore.isLoading ? (
              <EquipmentsContainer>
                {options.length ? (
                  <InfiniteScroll
                    dataLength={options.length}
                    hasMore={ContentStudioEquipmentStore.hasMoreInfo}
                    next={this.fetchMoreEquipments}
                    height={`calc(100vh - ${FORM_INFORMATION_HEIGHT})`}
                    loader={<ScrollLoader />}
                  >
                    {options.map(option => (
                      <EquipmentWrapper
                        key={option.value.id}
                        checked={this.isOptionChecked(option)}
                      >
                        <EquipmentInfo>
                          <EquipmentIcon image={option.value.imageUrl} />
                          <TrimmedText lines={1} text={option.label}>
                            <Typography variant="subtitle1">
                              {option.label}
                            </Typography>
                          </TrimmedText>
                        </EquipmentInfo>
                        <Checkbox
                          color="primary"
                          onClick={this.onChangeCheckbox(option)}
                          checked={this.isOptionChecked(option)}
                        />
                      </EquipmentWrapper>
                    ))}
                  </InfiniteScroll>
                ) : (
                  <Placeholder
                    height={132}
                    width={132}
                    text={
                      ContentStudioEquipmentStore.search
                        ? "No matches found. Please try another search."
                        : "No equipment added yet."
                    }
                  />
                )}
              </EquipmentsContainer>
            ) : (
              <Loader />
            )}
          </Row>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={this.onSaveNewEquipment}
              fullWidth
            >
              Save
            </Button>
          </ButtonContainer>
        </Buttons>
      </>
    );
  }
}

export default withGlobalSnackbar(EquipmentForm);
